import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Sun, Moon, MapPin, Camera, Clock, Compass,
  ChevronRight, Star, Zap, Cloud, Eye
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const LandingPage = () => {
  const { t } = useI18n();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      icon: Sun,
      title: t('landing.featureSunTitle'),
      description: t('landing.featureSunDesc'),
      gradient: "gradient-golden-hour"
    },
    {
      icon: Zap,
      title: t('shotScore.title'),
      description: t('landing.featureScoreDesc'),
      gradient: "gradient-milky-way"
    },
    {
      icon: MapPin,
      title: t('landing.featureScoutingTitle'),
      description: t('landing.featureScoutingDesc'),
      gradient: "gradient-blue-hour"
    },
    {
      icon: Cloud,
      title: t('weather.title'),
      description: t('landing.featureWeatherDesc'),
      gradient: "gradient-golden-hour"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Noise texture */}
      <div className="noise-overlay absolute inset-0 pointer-events-none" />
      
      {/* Background image */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1616049997556-524fbfe276db?crop=entropy&cs=srgb&fm=jpg&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950" />

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg gradient-golden-hour flex items-center justify-center">
            <Sun className="w-6 h-6 text-black" />
          </div>
          <span className="font-heading font-semibold text-xl text-white">LuminaSpot</span>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-4"
        >
          <Link to="/login">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5" data-testid="nav-login-btn">
              {t('auth.login')}
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider text-sm px-6 btn-tactile" data-testid="nav-register-btn">
              {t('landing.getStartedNav')}
            </Button>
          </Link>
        </motion.div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 text-sm font-medium">{t('landing.badge')}</span>
          </div>

          <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
            {t('landing.heroLine1')}
            <span className="block text-amber-500 glow-text">{t('landing.heroLine2')}</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-xl leading-relaxed">
            {t('landing.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register">
              <Button
                size="lg"
                className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider px-8 py-6 text-base btn-tactile w-full sm:w-auto"
                data-testid="hero-get-started-btn"
              >
                {t('landing.getStarted')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="outline"
                size="lg"
                className="border-white/20 text-white hover:bg-white/5 hover:border-white/40 rounded-none uppercase tracking-wider px-8 py-6 text-base w-full sm:w-auto"
                data-testid="hero-login-btn"
              >
                {t('landing.haveAccount')}
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="grid grid-cols-3 gap-8 mt-20 max-w-xl"
        >
          {[
            { value: "24h", label: t('landing.statWeather') },
            { value: "100%", label: t('landing.statAccuracy') },
            { value: "∞", label: t('landing.statLocations') }
          ].map((stat, i) => (
            <div key={i} className="text-center sm:text-left">
              <div className="font-mono text-3xl md:text-4xl font-bold text-amber-500">{stat.value}</div>
              <div className="text-slate-500 text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-semibold text-3xl md:text-4xl text-white mb-4">
              {t('landing.features')}
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                className="group relative p-6 rounded-xl bg-slate-900/50 border border-white/10 hover:border-amber-500/30 transition-all duration-300 card-interactive"
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-12 h-12 rounded-lg ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-black" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                
                {hoveredFeature === index && (
                  <motion.div 
                    layoutId="feature-glow"
                    className="absolute inset-0 rounded-xl border-2 border-amber-500/50 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-2xl overflow-hidden"
          >
            <div 
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: `url(https://images.unsplash.com/photo-1603453888566-1fad5aa24b7f?crop=entropy&cs=srgb&fm=jpg&q=85)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/70" />
            
            <div className="relative p-12 md:p-16">
              <div className="max-w-xl">
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                  {t('landing.ctaTitlePrefix')} <span className="text-amber-500">{t('landing.ctaTitleHighlight')}</span>?
                </h2>
                <p className="text-slate-300 text-lg mb-8">
                  {t('landing.ctaSubtitle')}
                </p>
                <Link to="/register">
                  <Button
                    size="lg"
                    className="bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-none uppercase tracking-wider px-8 py-6 btn-tactile"
                    data-testid="cta-register-btn"
                  >
                    {t('landing.createFreeAccount')}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg gradient-golden-hour flex items-center justify-center">
                <Sun className="w-4 h-4 text-black" />
              </div>
              <span className="font-heading font-medium text-white">LuminaSpot</span>
            </div>
            <p className="text-slate-500 text-sm">
              {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
